import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  Vault,
  VaultDetail,
  VaultMember,
  VaultAddress,
  VaultBalance,
  VaultTransaction,
  VaultBalanceHistory,
  TxProposal,
  SigningRequest,
  PendingSetup,
  NetworkFee,
  VaultToken,
  VaultPolicy,
  VelocityUsage,
  TokenThreatInfo,
  FlaggedAddress,
} from '@/types/vault';

interface VaultState {
  vaults: Vault[];
  currentVault: VaultDetail | null;
  vaultMembers: VaultMember[];
  vaultAddresses: VaultAddress[];
  vaultBalances: VaultBalance[];
  vaultTransactions: VaultTransaction[];
  vaultBalanceHistory: VaultBalanceHistory[];
  txProposals: TxProposal[];
  watchedTokens: VaultToken[];
  fluxNodeCount: number;
  pendingSetups: PendingSetup[];
  signingRequests: SigningRequest[];
  signingRequestCount: number;
  networkFees: NetworkFee[];
  cryptoRates: Record<string, number>;
  vaultPolicy: VaultPolicy | null;
  velocityUsage: VelocityUsage | null;
  tokenThreats: TokenThreatInfo[];
  flaggedAddresses: FlaggedAddress[];
  isLoading: boolean;
  error: string | null;
}

const initialState: VaultState = {
  vaults: [],
  currentVault: null,
  vaultMembers: [],
  vaultAddresses: [],
  vaultBalances: [],
  vaultTransactions: [],
  vaultBalanceHistory: [],
  txProposals: [],
  watchedTokens: [],
  fluxNodeCount: 0,
  pendingSetups: [],
  signingRequests: [],
  signingRequestCount: 0,
  networkFees: [],
  cryptoRates: {},
  vaultPolicy: null,
  velocityUsage: null,
  tokenThreats: [],
  flaggedAddresses: [],
  isLoading: false,
  error: null,
};

const vaultSlice = createSlice({
  name: 'vault',
  initialState,
  reducers: {
    // Vaults
    setVaults: (state, action: PayloadAction<Vault[]>) => {
      state.vaults = action.payload;
      state.isLoading = false;
      state.error = null;
    },

    addVault: (state, action: PayloadAction<Vault>) => {
      state.vaults.unshift(action.payload);
    },

    updateVault: (state, action: PayloadAction<Vault>) => {
      const index = state.vaults.findIndex((v) => v.id === action.payload.id);
      if (index !== -1) {
        state.vaults[index] = action.payload;
      }
      if (state.currentVault?.id === action.payload.id) {
        state.currentVault = {
          ...state.currentVault,
          ...action.payload,
        };
      }
    },

    removeVault: (state, action: PayloadAction<string>) => {
      state.vaults = state.vaults.filter((v) => v.id !== action.payload);
      if (state.currentVault?.id === action.payload) {
        state.currentVault = null;
      }
    },

    archiveVaultById: (state, action: PayloadAction<string>) => {
      const vault = state.vaults.find((v) => v.id === action.payload);
      if (vault) {
        vault.status = 'archived';
      }
      if (state.currentVault?.id === action.payload) {
        state.currentVault.status = 'archived';
      }
    },

    unarchiveVaultById: (state, action: PayloadAction<string>) => {
      const vault = state.vaults.find((v) => v.id === action.payload);
      if (vault) {
        vault.status = 'active';
      }
      if (state.currentVault?.id === action.payload) {
        state.currentVault.status = 'active';
      }
    },

    freezeVaultById: (state, action: PayloadAction<string>) => {
      const vault = state.vaults.find((v) => v.id === action.payload);
      if (vault) {
        vault.status = 'frozen';
      }
      if (state.currentVault?.id === action.payload) {
        state.currentVault.status = 'frozen';
      }
    },

    unfreezeVaultById: (state, action: PayloadAction<string>) => {
      const vault = state.vaults.find((v) => v.id === action.payload);
      if (vault) {
        vault.status = 'active';
      }
      if (state.currentVault?.id === action.payload) {
        state.currentVault.status = 'active';
      }
    },

    // Current vault
    setCurrentVault: (state, action: PayloadAction<VaultDetail>) => {
      state.currentVault = action.payload;
      state.isLoading = false;
    },

    clearCurrentVault: (state) => {
      state.currentVault = null;
      state.vaultMembers = [];
      state.vaultAddresses = [];
      state.vaultBalances = [];
      state.vaultTransactions = [];
      state.vaultBalanceHistory = [];
      state.txProposals = [];
      state.watchedTokens = [];
      state.fluxNodeCount = 0;
      state.vaultPolicy = null;
      state.velocityUsage = null;
    },

    // Members
    setVaultMembers: (state, action: PayloadAction<VaultMember[]>) => {
      state.vaultMembers = action.payload;
    },

    addVaultMember: (state, action: PayloadAction<VaultMember>) => {
      state.vaultMembers.push(action.payload);
    },

    removeVaultMember: (state, action: PayloadAction<string>) => {
      state.vaultMembers = state.vaultMembers.filter((m) => m.id !== action.payload);
    },

    // Addresses
    setVaultAddresses: (state, action: PayloadAction<VaultAddress[]>) => {
      state.vaultAddresses = action.payload;
    },

    addVaultAddress: (state, action: PayloadAction<VaultAddress>) => {
      state.vaultAddresses.push(action.payload);
    },

    // Balances
    setVaultBalances: (state, action: PayloadAction<VaultBalance[]>) => {
      state.vaultBalances = action.payload;
    },

    // Transactions
    setVaultTransactions: (state, action: PayloadAction<VaultTransaction[]>) => {
      state.vaultTransactions = action.payload;
    },

    // Balance History
    setVaultBalanceHistory: (state, action: PayloadAction<VaultBalanceHistory[]>) => {
      state.vaultBalanceHistory = action.payload;
    },

    // Network Fees
    setNetworkFees: (state, action: PayloadAction<NetworkFee[]>) => {
      state.networkFees = action.payload;
    },

    // Crypto Rates
    setCryptoRates: (state, action: PayloadAction<Record<string, number>>) => {
      state.cryptoRates = action.payload;
    },

    // Watched Tokens
    setWatchedTokens: (state, action: PayloadAction<VaultToken[]>) => {
      state.watchedTokens = action.payload;
    },

    addWatchedToken: (state, action: PayloadAction<VaultToken>) => {
      state.watchedTokens.push(action.payload);
    },

    removeWatchedToken: (state, action: PayloadAction<string>) => {
      // Exact-match: callers always pass the canonical contract string from
      // state, so case folding is unnecessary and would be incorrect for
      // case-significant Solana SPL mints (base58).
      state.watchedTokens = state.watchedTokens.filter((t) => t.contract !== action.payload);
    },

    // Flux Nodes
    setFluxNodeCount: (state, action: PayloadAction<number>) => {
      state.fluxNodeCount = action.payload;
    },

    // Policy
    setVaultPolicy: (state, action: PayloadAction<VaultPolicy | null>) => {
      state.vaultPolicy = action.payload;
    },

    setVelocityUsage: (state, action: PayloadAction<VelocityUsage | null>) => {
      state.velocityUsage = action.payload;
    },

    // Proposals
    setTxProposals: (state, action: PayloadAction<TxProposal[]>) => {
      state.txProposals = action.payload;
    },

    addTxProposal: (state, action: PayloadAction<TxProposal>) => {
      state.txProposals.unshift(action.payload);
    },

    updateTxProposal: (state, action: PayloadAction<TxProposal>) => {
      const index = state.txProposals.findIndex((p) => p.id === action.payload.id);
      if (index !== -1) {
        state.txProposals[index] = action.payload;
      }
    },

    // Pending setups
    setPendingSetups: (state, action: PayloadAction<PendingSetup[]>) => {
      state.pendingSetups = action.payload;
    },

    removePendingSetup: (state, action: PayloadAction<string>) => {
      state.pendingSetups = state.pendingSetups.filter((s) => s.vaultId !== action.payload);
    },

    setSigningRequests: (state, action: PayloadAction<SigningRequest[]>) => {
      state.signingRequests = action.payload;
      state.signingRequestCount = action.payload.length;
    },

    setSigningRequestCount: (state, action: PayloadAction<number>) => {
      state.signingRequestCount = action.payload;
    },

    // Loading/Error
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    // Token Threats
    setTokenThreats: (state, action: PayloadAction<TokenThreatInfo[]>) => {
      state.tokenThreats = action.payload;
    },
    setFlaggedAddresses: (state, action: PayloadAction<FlaggedAddress[]>) => {
      state.flaggedAddresses = action.payload;
    },

    // Reset
    reset: () => {
      return initialState;
    },
  },
});

export const {
  setVaults,
  addVault,
  updateVault,
  removeVault,
  archiveVaultById,
  unarchiveVaultById,
  freezeVaultById,
  unfreezeVaultById,
  setCurrentVault,
  clearCurrentVault,
  setVaultMembers,
  addVaultMember,
  removeVaultMember,
  setVaultAddresses,
  addVaultAddress,
  setVaultBalances,
  setVaultTransactions,
  setVaultBalanceHistory,
  setNetworkFees,
  setCryptoRates,
  setWatchedTokens,
  addWatchedToken,
  removeWatchedToken,
  setFluxNodeCount,
  setVaultPolicy,
  setVelocityUsage,
  setTxProposals,
  addTxProposal,
  updateTxProposal,
  setPendingSetups,
  removePendingSetup,
  setSigningRequests,
  setSigningRequestCount,
  setTokenThreats,
  setFlaggedAddresses,
  setLoading,
  setError,
  reset,
} = vaultSlice.actions;

export default vaultSlice.reducer;
